/**
 * The weather, as the game reads it (T103 piece ③).
 *
 * **D57** — each game year draws a different real Icelandic year.
 * **D58** — Iceland is five zones and you get the one you are standing in.
 * [ADR-0013](../../../Project/adr/0013-five-weather-zones.md).
 *
 * This file is the pure half: **which zone is this position in, and what does a
 * row of the table mean.** Reading the files and drawing the year belong to the
 * server — see `packages/server/src/weather.ts`.
 */

import zonesData from '../data/weather/zones.json' with { type: 'json' };
import { t } from './i18n.ts';

/** One of the five zones, with its station already projected into ISN93. */
export interface WeatherZone {
  readonly key: string;
  readonly name: string;
  readonly station: number;
  readonly stationName: string;
  /** ISN93 easting of the **station**. Kept for the record; not what picks a zone. */
  readonly x: number;
  /** ISN93 **northing** of the station. ⚠️ The world's z is the NEGATED northing. */
  readonly northing: number;
  /**
   * 🔴 **The centre of the REGION, and this is what decides which zone you are
   * in** — not the station.
   *
   * The first version measured to the stations and the zones came out wrong:
   * **the central highland fell to Reykjavík** (130 km, against 180 km to
   * Grímsstaðir) and **Húsavík fell to the highland**. Five instruments sited for
   * other reasons do not partition a country.
   *
   * So the two jobs are separate, which is also the honest structure: **the
   * centre says where the region is, the station says what the weather was
   * there.**
   */
  readonly centreX: number;
  readonly centreNorthing: number;
  readonly note: string;
}

export const WEATHER_ZONES: readonly WeatherZone[] = zonesData.zones;

/** The years the world may draw from — thirty of them, 1996–2025. */
export const WEATHER_POOL_FROM = zonesData.poolFrom;
export const WEATHER_POOL_TO = zonesData.poolTo;
/** Hours between rows in a table: 3. */
export const WEATHER_STEP_HOURS = zonesData.hoursStep;

export function weatherPool(): number[] {
  const out: number[] = [];
  for (let y = WEATHER_POOL_FROM; y <= WEATHER_POOL_TO; y++) out.push(y);
  return out;
}

/**
 * How wide the blend between two zones is, in metres.
 *
 * 🔴 **haugur asked for a "short blend"** rather than a hard switch, because a
 * step across an invisible line that changes the sky reads as a bug.
 *
 * **5,000 m gives a 10 km band** — the zones are tens to hundreds of kilometres
 * apart, so on the scale of Iceland this is short, and on the scale of a walking
 * player it is long enough that the change is never abrupt. ⚠️ **It is a
 * judgement, not a measurement**, and it is the one number here nobody can check
 * against an instrument.
 */
export const ZONE_BLEND_METRES = 5000;

/** A zone and how much of it applies here. Weights sum to 1. */
export interface ZoneShare {
  readonly zone: WeatherZone;
  readonly weight: number;
}

/**
 * Which zone — or which two — a world position belongs to.
 *
 * @param x ISN93 easting
 * @param z **world z**, which is the negated northing
 *
 * ⚠️ **Nearest region CENTRE wins**, compared in plain ISN93 metres — not the
 * nearest station, which puts the highland in Reykjavík. There are no boundary
 * polygons to source or maintain, and the line lands roughly where the mountains
 * put it anyway (ADR-0013). Near that line the two nearest zones are mixed, so
 * nothing changes in a single step.
 */
export function zonesAt(x: number, z: number): ZoneShare[] {
  const northing = -z;

  const ranked = WEATHER_ZONES.map((zone) => ({
    zone,
    /* ⚠️ To the region's CENTRE, never to its station. See `centreX`. */
    d: Math.hypot(zone.centreX - x, zone.centreNorthing - northing),
  })).sort((a, b) => a.d - b.d);

  const nearest = ranked[0];
  const second = ranked[1];
  /* One zone in the list would make this unreachable; the data has five. */
  if (!nearest) return [];
  if (!second) return [{ zone: nearest.zone, weight: 1 }];

  const gap = second.d - nearest.d;
  if (gap >= ZONE_BLEND_METRES) return [{ zone: nearest.zone, weight: 1 }];

  /* Exactly equidistant → half each; a full blend-width apart → all the nearest. */
  const nearWeight = 0.5 + gap / (2 * ZONE_BLEND_METRES);
  return [
    { zone: nearest.zone, weight: nearWeight },
    { zone: second.zone, weight: 1 - nearWeight },
  ];
}

/** What the weather is doing. Any field may be absent — see the CSV header. */
export interface Weather {
  /** °C. */
  readonly temperature: number | null;
  /** Mean wind, m/s. */
  readonly wind: number | null;
  /** Gust, m/s. */
  readonly gust: number | null;
  /** Degrees the wind blows FROM, or null when it was variable or calm. */
  readonly direction: number | null;
  /** Precipitation in the observation period, mm. */
  readonly rain: number | null;
  /**
   * Cloud cover in oktas, **0–8**, or null when the sky was obscured.
   *
   * ⚠️ **Obscured is not "more than overcast"** — it is fog, and it is carried
   * separately in {@link Weather.fog}. Folding the code 9 into this number would
   * make a foggy day the cloudiest possible one, which is nearly the opposite of
   * what it looks like.
   */
  readonly cloud: number | null;
  /** The sky was obscured — fog. `Design/00_Visual_Language.md` §12.6 has its colour. */
  readonly fog: boolean;
  /** Snow lying on the ground, cm. */
  readonly snowDepth: number | null;
}

/** Oktas in a completely covered sky. The scale is eighths, so: 8. */
export const OKTAS_FULL = 8;

/**
 * How covered the sky is, as 0 (clear) to 1 (completely covered).
 *
 * 🔴 **A missing reading answers 1, not 0.5 and not 0.** Design §12.6 (V17)
 * measured haugur's own photographs and found **overcast is the default
 * Icelandic sky** — five of the nine daylight frames are overcast, fog or storm.
 * So "we do not know" resolves to the state the country is usually in, and the
 * failure mode of a broken data build is a grey day rather than a Mediterranean
 * one.
 */
export function cloudiness(weather: Weather | null): number {
  const cloud = weather?.cloud;
  if (cloud === null || cloud === undefined) return 1;
  return Math.min(1, Math.max(0, cloud / OKTAS_FULL));
}

/**
 * Whether precipitation is falling as snow rather than rain.
 *
 * ⚠️ **A judgement, not a measurement.** The observation records how much fell,
 * never what form it took, so this is inferred from temperature. **0.5 °C** is
 * the threshold: snow reaching the ground above about 2 °C is unusual, below 0 it
 * is the norm, and slyddu — sleet — occupies the middle with no way to tell it
 * apart from here. Chosen low so the game says *rigning* when it is uncertain,
 * because rain is the commoner Icelandic answer at every altitude the world
 * currently reaches.
 */
export function fallingAsSnow(weather: Weather | null): boolean {
  const t = weather?.temperature;
  return t !== null && t !== undefined && t <= 0.5;
}

/**
 * What the sky is doing, in the word an Icelander would use.
 *
 * ✅ **The three anchors are Veðurstofa's own**, from their cloud-cover guidance:
 * **0/8 heiðskírt · 4/8 hálfskýjað · 8/8 alskýjað**. ⚠️ **The two intermediate
 * bands — léttskýjað and skýjað — are the conventional reading of the same
 * scale and were NOT confirmed from that page.** They are marked rather than
 * asserted; if haugur says the bands sit elsewhere, he is the source.
 *
 * ⚠️ **Order matters.** Fog wins over cloud because in fog there is no sky to
 * describe, and precipitation wins over cloud because "it is raining" is the more
 * useful thing to be told than how many eighths are covered while it does.
 *
 * ⚠️ **The words go through the language file (T124), and their values there are
 * still Icelandic.** They are Veðurstofa's own vocabulary and showing them
 * verbatim is the point; keying them only means a future translator gets to
 * decide that, rather than the decision being welded into this function.
 */
export function describeSky(weather: Weather | null): string {
  if (!weather) return t('common.unknown');
  if (weather.fog) return t('weather.fog');
  if (weather.rain !== null && weather.rain > 0) {
    return fallingAsSnow(weather) ? t('weather.snow') : t('weather.rain');
  }

  const cloud = weather.cloud;
  if (cloud === null) return t('common.unknown');
  if (cloud < 0.5) return t('weather.clear');
  if (cloud < 2.5) return t('weather.lightCloud');
  if (cloud < 4.5) return t('weather.halfCloud');
  if (cloud < 7.5) return t('weather.cloudy');
  return t('weather.overcast');
}

/** SYNOP's code for "sky obscured", which is fog. Oktas stop at 8. */
const CLOUD_OBSCURED = 9;

const field = (v: string | undefined): number | null => {
  if (v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Parse one `doy,hour,t,f,fx,dir,r,n,snd` row.
 *
 * Returns `null` for anything malformed rather than a half-built reading —
 * a row that silently became "0 °C, no wind" would be a calm winter day the
 * instruments never saw.
 */
export function parseWeatherRow(
  line: string,
): { doy: number; hour: number; weather: Weather } | null {
  const parts = line.split(',');
  if (parts.length < 9) return null;

  const doy = field(parts[0]);
  const hour = field(parts[1]);
  if (doy === null || hour === null) return null;

  const cloudCode = field(parts[7]);

  return {
    doy,
    hour,
    weather: {
      temperature: field(parts[2]),
      wind: field(parts[3]),
      gust: field(parts[4]),
      direction: field(parts[5]),
      rain: field(parts[6]),
      cloud: cloudCode === CLOUD_OBSCURED ? null : cloudCode,
      fog: cloudCode === CLOUD_OBSCURED,
      snowDepth: field(parts[8]),
    },
  };
}

/** Average of two numbers that may be missing. Absent + present = present. */
const mix = (a: number | null, b: number | null, w: number): number | null => {
  if (a === null) return b;
  if (b === null) return a;
  return a * w + b * (1 - w);
};

/**
 * Blend the weather of two zones near their boundary.
 *
 * ⚠️ **Fog does not blend — it is taken from whichever zone weighs more.** It is
 * a state rather than a quantity, and "40% fog" is not a sky.
 */
export function blendWeather(
  shares: readonly { weather: Weather; weight: number }[],
): Weather | null {
  const first = shares[0];
  if (!first) return null;
  const second = shares[1];
  if (!second) return first.weather;

  const w = first.weight;
  return {
    temperature: mix(first.weather.temperature, second.weather.temperature, w),
    wind: mix(first.weather.wind, second.weather.wind, w),
    gust: mix(first.weather.gust, second.weather.gust, w),
    /* ⚠️ Direction is an ANGLE and does not average: north-west (315°) and
       north-east (45°) average to south (180°), the exact opposite. Taken from
       the heavier zone instead. */
    direction: w >= 0.5 ? first.weather.direction : second.weather.direction,
    rain: mix(first.weather.rain, second.weather.rain, w),
    cloud: mix(first.weather.cloud, second.weather.cloud, w),
    fog: w >= 0.5 ? first.weather.fog : second.weather.fog,
    snowDepth: mix(first.weather.snowDepth, second.weather.snowDepth, w),
  };
}
